import { color } from "highcharts";
import { colors } from "styles/colors";
import { HIRING_STATUS_COLORS } from "./hiringStatusColors";

export const statusArr = [
  { id: 4, name: "Applied", color: "#007BFF" },
  { id: 5, name: "Shortlisted", color: "#FFA500" },
  { id: 6, name: "Interviewed", color: "#FE6A33" },
  { id: 18, name: "Selected", color: "#6F42C1" },
  { id: 7, name: "Offered", color: "#28A745" },
  { id: 13, name: "Joined", color: "#006400" },
  { id: 9, name: "Rejected", color: "#DC3545" }
];

// Status progression order (excluding Rejected which can happen at any stage)
export const statusProgressionOrder = [4, 5, 6, 18, 7, 13];
// Applied → Shortlisted → Interviewed → Selected → Offered → Joined

/**
 * Get available statuses based on current status with progression rules:
 * - All previous statuses in progression are disabled
 * - Rejected (9) is always enabled unless current status is Rejected
 * - If current status is Rejected, all other statuses are disabled (final state)
 */
export const getAvailableStatuses = (currentStatusId: number) => {
  // If current status is Rejected (9), all other statuses should be disabled
  if (currentStatusId === 9) {
    return statusArr.map(status => ({
      ...status,
      disabled: status.id !== 9
    }));
  }

  // Find current position in progression
  const currentIndex = statusProgressionOrder.indexOf(currentStatusId);

  return statusArr.map(status => {
    // Rejected is always enabled (unless current is already Rejected, handled above)
    if (status.id === 9) {
      return { ...status, disabled: false };
    }

    // Find position of this status in progression
    const statusIndex = statusProgressionOrder.indexOf(status.id);

    // Disable if this status comes before current status in progression
    return {
      ...status,
      disabled: statusIndex < currentIndex
    };
  });
};

/**
 * Check if status transition is valid based on progression rules
 */
export const isValidStatusTransition = (
  fromStatusId: number,
  toStatusId: number
): boolean => {
  // If trying to move FROM Rejected, deny (Rejected is final)
  if (fromStatusId === 9) {
    return false;
  }

  // Can always move TO Rejected
  if (toStatusId === 9) {
    return true;
  }

  // Get indices in progression
  const fromIndex = statusProgressionOrder.indexOf(fromStatusId);
  const toIndex = statusProgressionOrder.indexOf(toStatusId);

  // Can only move forward or stay same (not backward)
  return toIndex >= fromIndex;
};

export const placementStatusArr = [
  { id: 1, name: "Shortlisted", color: colors.chipYellow },
  { id: 2, name: "Cleared", color: colors.chipGreen },
  { id: 3, name: "Rejected", color: colors.chipRed },
  { id: 4, name: "Selected", color: colors.chipBlue }
];

export const jobStatusArr = [
  { id: 1, name: "Active" },
  { id: 21, name: "Inactive" },
  { id: 0, name: "Pending Approval" },
  { id: 16, name: "Closed" },
  { id: 22, name: "Expired" }
];

export const userRoles = [
  {
    id: "employer_user",
    name: "Admin"
  },
  {
    id: "sub_user",
    name: "Sub User"
  }
];

export const sortByArr = [
  { id: 1, name: "Latest" },
  { id: 2, name: "Oldest" }
];

export const sortByArr2 = [
  { id: "-created_at", name: "Latest" },
  { id: "created_at", name: "Oldest" }
];

export const statusMappingBg = {
  4: HIRING_STATUS_COLORS[4], // Applied - blue
  5: HIRING_STATUS_COLORS[5], // Shortlisted - orange
  6: HIRING_STATUS_COLORS[6], // Interviewed - orange-red
  7: HIRING_STATUS_COLORS[7], // Offered - purple
  18: HIRING_STATUS_COLORS[18], // Selected - green
  13: HIRING_STATUS_COLORS[13], // Joined - dark green
  9: HIRING_STATUS_COLORS[9], // Rejected - red
  1: colors.chipYellow, // Shortlisted - yellow (different context)
  2: colors.chipGreen, // Cleared - green  
  3: colors.chipRed, // Rejected - red (different context)
  21: "#DC3545", // Inactive
  0: "#007BFF", // Pending Approval
  16: "#DC3545", // Closed
  22: "#DC3545" // Expired
};

export const statusMappingDisplay = {
  1: "active",
  3: "deleted",
  4: "applied",
  5: "shortlisted",
  6: "interviewed",
  7: "offered",
  13: "joined",
  9: "rejected",
  17: "draft"
};

export const statusMapping = {
  0: "Pending Approval",
  1: "Active",
  3: "Deleted",
  4: "Applied",
  5: "Shortlisted",
  6: "Interviewed",
  7: "Offered",
  13: "Joined",
  9: "Rejected",
  18: "Selected",
  17: "Draft",
  16: "Closed",
  20: "Cleared",
  15: "Put On Hold",
  21: "Inactive",
  22: "Expired"
};

// Student hiring status constants - reusable across student-facing components
export const studentHiringStatusIds = [4, 5, 6, 7, 9, 13, 18];

export const studentHiringStatusMapping = {
  4: "Applied",
  5: "Shortlisted",
  6: "Interviewed",
  7: "Offered",
  9: "Rejected",
  13: "Joined",
  18: "Selected"
};

export const studentHiringStatusBgMapping = {
  4: HIRING_STATUS_COLORS[4],  // Applied - blue
  5: HIRING_STATUS_COLORS[5],  // Shortlisted - orange          
  6: HIRING_STATUS_COLORS[6],  // Interviewed - orange-red            
  7: HIRING_STATUS_COLORS[7],  // Offered - purple          
  9: HIRING_STATUS_COLORS[9],  // Rejected - red          
  13: HIRING_STATUS_COLORS[13], // Joined - dark green         
  18: HIRING_STATUS_COLORS[18]  // Selected - green          
};

// Job statuses where withdrawal is allowed (in-process statuses only)
// Excludes final statuses like Rejected (9) and Joined (13)
export const withdrawableJobStatuses = [4, 5, 6, 7, 18];

// Helper function to check if a status should be shown to students
export const isStudentHiringStatus = (statusId: number): boolean => {
  return studentHiringStatusIds.includes(statusId);
};

export const jobOrInternshipArr = [
  { id: "INTERNSHIP", name: "Internship" },
  { id: "JOB", name: "Job" }
];

export const genders = {
  M: "Male",
  F: "Female",
  O: "Others",
  B: "Both",
  NA: "No Preference"
};

export const gendersArr = [
  { id: "M", name: "Male" },
  { id: "F", name: "Female" },
  { id: "O", name: "Others" },
  { id: "B", name: "Both" },
  { id: "NA", name: "No Preference" }
];

export const gendersArrForStudentRegistration = [
  { id: "M", name: "Male" },
  { id: "F", name: "Female" },
  { id: "O", name: "Others" }
];

export const jobModes = {
  FULL_TIME: "Full Time",
  PART_TIME: "Part Time"
};

export const experienceDropdown = [
  { id: 1, name: "Fresher", min: 0 },
  { id: 2, name: "0 - 1 Year", min: 0, max: 1 },
  { id: 3, name: "1 - 2 Years", min: 1, max: 2 },
  { id: 4, name: "3+ Years", min: 3 }
];

export const jobModesArr = [
  { id: "FULL_TIME", name: "Full Time" },
  { id: "PART_TIME", name: "Part Time" }
];

export const jobTeamTypes = {
  IN_OFFICE: "In Office",
  WORK_FROM_HOME: "Remote",
  HYBRID: "Hybrid"
};

export const jobTeamTypesArr = [
  { id: "IN_OFFICE", name: "In Office" },
  { id: "WORK_FROM_HOME", name: "Remote" },
  { id: "HYBRID", name: "Hybrid" }
];

export const ownershipArr = [
  { id: "CENTRAL_OPEN_UNIVERSITY", name: "Central Open University" },
  { id: "CENTRAL_UNIVERSITY", name: "Central University" },
  { id: "DEEMED_UNIVERSITY_GOVERNMENT", name: "Deemed University- Government" },
  {
    id: "DEEMED_UNIVERSITY_GOVERNMENT_AIDED",
    name: "DEEMED_UNIVERSITY_GOVERNMENT_AIDED"
  },
  { id: "DEEMED_UNIVERSITY_PRIVATE", name: "Deemed University - Private" },
  {
    id: "INSTITUTE_OF_NATIONAL_IMPORTANCE",
    name: "INSTITUTE_OF_NATIONAL_IMPORTANCE"
  },
  {
    id: "INSTITUTE_UNDER_STATE_LEGISLATURE_ACT",
    name: "Institute Under State Legislature Act"
  },
  { id: "STATE_OPEN_UNIVERSITY", name: "State Open University" },
  {
    id: "STATE_PRIVATE_OPEN_UNIVERSITY",
    name: "State Private Open University"
  },
  { id: "STATE_PRIVATE_UNIVERSITY", name: "State Private University" },
  { id: "STATE_PUBLIC_UNIVERSITY", name: "State Public University" },
  { id: "OTHER", name: "Other" }
];

const MIMETypesMapping = {
  ".apng": "apng",
  ".avif": "avif",
  ".gif": "gif",
  ".jpg": "jpeg",
  ".jpeg": "jpeg",
  ".jfif": "jpeg",
  ".pjpeg": "jpeg",
  ".pjp": "jpeg",
  ".png": "png",
  ".svg": "svg+xml",
  ".webp": "webp",
  ".pdf": "pdf",
  ".mp4": "mp4",
  ".mov": "quicktime",
  ".webm": "webm"
};

export const getMIMETypeOfFile = (file_type) => {
  return MIMETypesMapping[file_type];
};

export const acceptImageTypes =
  ".apng, .avif, .gif, .jpg, .jpeg, .jfif, .pjpeg, .pjp, .png, .svg, .webp";

export const acceptVideoTypes = ".mp4, .mov, .webm";

export const broadCastCategories = [
  { id: "GENERAL", name: "General" },
  { id: "PLACEMENT_DRIVE", name: "Placement Drive" },
  { id: "NEW_COURSE", name: "New Course" },
  { id: "WEBINAR", name: "Webinar" },
  { id: "EVENTS", name: "Events" }
];

export const broadCastCategoriesTypes = {
  GENERAL: "General",
  PLACEMENT_DRIVE: "Placement Drive",
  NEW_COURSE: "New Course",
  WEBINAR: "Webinar",
  EVENTS: "Events"
};

export const broadCastTargetGroup = [
  { id: "STUDENTS", name: "Students" },
  { id: "INSTITUTIONS", name: "Institutions" },
  { id: "EMPLOYERS", name: "Employers" }
];

export const broadCastTargetGroupTypes = {
  STUDENTS: "Students",
  INSTITUTIONS: "Institutions",
  EMPLOYERS: "Employers"
};
